/**
 * The editable resources, declared once for the UI.
 *
 * These mirror the server declarations in Modules/CrudModule.cs. The server is still
 * the authority — it re-validates everything and owns the rules — but declaring the
 * shape here lets one page render a usable form for every table.
 */
import type { ResourceDef } from '../ui/ResourcePage'

const ALTITUDE = [
  { value: 'lowland', label: 'Lowland' },
  { value: 'midland', label: 'Midland' },
  { value: 'highland', label: 'Highland' },
]

const CROP_CATEGORY = [
  { value: 'vegetable', label: 'Vegetable' },
  { value: 'fruit', label: 'Fruit' },
  { value: 'leafy', label: 'Leafy' },
  { value: 'herb', label: 'Herb' },
  { value: 'root', label: 'Root' },
  { value: 'legume', label: 'Legume' },
  { value: 'cereal', label: 'Cereal' },
  { value: 'other', label: 'Other' },
]

const OWNERSHIP = [
  { value: 'owned', label: 'Owned' },
  { value: 'contracted', label: 'Contracted' },
  { value: 'hired', label: 'Hired per trip' },
]

const CESS_BASIS = [
  { value: 'per_kg', label: 'Per kilo' },
  { value: 'per_tonne', label: 'Per tonne' },
  { value: 'per_package', label: 'Per package' },
  { value: 'per_trip', label: 'Per trip' },
  { value: 'per_vehicle', label: 'Per vehicle' },
]

const TENURE = [
  { value: 'owner', label: 'Owner' },
  { value: 'tenant', label: 'Tenant' },
  { value: 'sharecropper', label: 'Sharecropper' },
  { value: 'family', label: 'Family land' },
  { value: 'cooperative', label: 'Cooperative' },
  { value: 'unknown', label: 'Unknown' },
]

const PAYOUT_CHANNEL = [
  { value: 'mpesa', label: 'M-Pesa' },
  { value: 'bank', label: 'Bank transfer' },
  { value: 'cash', label: 'Cash' },
]

const EXPENSE_CATEGORY = [
  { value: 'fuel', label: 'Fuel' },
  { value: 'vehicle_hire', label: 'Vehicle hire' },
  { value: 'driver', label: 'Driver' },
  { value: 'cess', label: 'County cess' },
  { value: 'permit', label: 'Permit' },
  { value: 'licence', label: 'Licence' },
  { value: 'depot_rent', label: 'Depot rent' },
  { value: 'labour', label: 'Labour' },
  { value: 'packaging', label: 'Packaging' },
  { value: 'crate', label: 'Crates' },
  { value: 'cold_storage', label: 'Cold storage' },
  { value: 'power', label: 'Power' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'airtime', label: 'Airtime' },
  { value: 'bank_charges', label: 'Bank charges' },
  { value: 'irrecoverable_vat', label: 'Irrecoverable input VAT' },
  { value: 'financing', label: 'Financing' },
  { value: 'other', label: 'Other' },
]

const CONTRACT_STATUS = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'terminated', label: 'Terminated' },
]

const PRICING_MODE = [
  { value: 'weekly_list', label: 'Weekly price list' },
  { value: 'fixed', label: 'Fixed price' },
  { value: 'index_linked', label: 'Index-linked' },
  { value: 'cost_plus', label: 'Cost plus' },
]

const SHRINK_BUCKET = [
  { value: 'grade_out', label: 'Grade out' },
  { value: 'spoilage', label: 'Spoilage' },
  { value: 'moisture', label: 'Moisture' },
  { value: 'handling', label: 'Handling' },
  { value: 'rejection', label: 'Rejection' },
  { value: 'theft', label: 'Theft' },
  { value: 'scale', label: 'Scale' },
  { value: 'unexplained', label: 'Unexplained' },
]

export const RESOURCES: Record<string, ResourceDef> = {
  // ============================================================ geography
  zones: {
    key: 'zones',
    title: 'Buying areas',
    singular: 'Buying area',
    description:
      'The areas you buy from. Buying across a few different areas means one bad spell of weather cannot wipe out your whole supply.',
    writeRoles: ['admin', 'ops'],
    hasActive: true,
    searchable: true,
    columns: ['code', 'name', 'countyCode', 'altitudeBand', 'farmerCount'],
    derived: [{ name: 'farmerCount', label: 'Farmers' }],
    fields: [
      { name: 'code', label: 'Code', required: true, hint: 'Short, stable. Used on route codes.' },
      { name: 'name', label: 'Name', required: true },
      { name: 'countyCode', label: 'County', type: 'ref', ref: { resource: 'reference/counties', labelField: 'name' } },
      { name: 'altitudeBand', label: 'Altitude band', type: 'select', options: ALTITUDE,
        hint: 'Drives seasonality spread — zones at different altitudes peak at different times.' },
      { name: 'lat', label: 'Latitude', type: 'decimal', step: 0.000001 },
      { name: 'lng', label: 'Longitude', type: 'decimal', step: 0.000001 },
      { name: 'isActive', label: 'Active', type: 'bool', default: true },
    ],
  },

  depots: {
    key: 'depots',
    title: 'Stores',
    singular: 'Store',
    description:
      'Where produce is sorted and moved on. Pick somewhere close to your buyers rather than somewhere cheap — minutes on the road cost more than rent.',
    writeRoles: ['admin', 'ops'],
    hasActive: true,
    searchable: true,
    columns: ['code', 'name', 'countyCode', 'capacityKg', 'hasColdRoom', 'lotCount'],
    derived: [{ name: 'lotCount', label: 'Lots' }],
    fields: [
      { name: 'code', label: 'Code', required: true },
      { name: 'name', label: 'Name', required: true },
      { name: 'countyCode', label: 'County', type: 'ref', ref: { resource: 'reference/counties', labelField: 'name' } },
      { name: 'capacityKg', label: 'Capacity', type: 'decimal', suffix: 'kg' },
      { name: 'hasColdRoom', label: 'Has a cold room', type: 'bool',
        hint: 'Only worth it for crops whose loss rate justifies the power bill.' },
      { name: 'coldRoomTargetC', label: 'Cold room target', type: 'decimal', step: 0.1, suffix: '°C' },
      { name: 'lat', label: 'Latitude', type: 'decimal', step: 0.000001 },
      { name: 'lng', label: 'Longitude', type: 'decimal', step: 0.000001 },
      { name: 'isActive', label: 'Active', type: 'bool', default: true },
    ],
  },

  'collection-centres': {
    key: 'collection-centres',
    title: 'Collection points',
    singular: 'Collection point',
    description:
      'Meeting points where farmers bring produce at an agreed time. Gathering it in one place first is far cheaper than driving to every farm.',
    writeRoles: ['admin', 'ops'],
    hasActive: true,
    searchable: true,
    columns: ['code', 'name', 'zoneName', 'opensAt', 'closesAt', 'allWeatherAccess'],
    derived: [{ name: 'zoneName', label: 'Zone' }],
    filters: [{ name: 'zoneId', label: 'zones', ref: { resource: 'zones' } }],
    fields: [
      { name: 'code', label: 'Code', required: true },
      { name: 'name', label: 'Name', required: true },
      { name: 'zoneId', label: 'Zone', type: 'ref', ref: { resource: 'zones' }, required: true },
      { name: 'opensAt', label: 'Opens', type: 'time' },
      { name: 'closesAt', label: 'Closes', type: 'time' },
      { name: 'allWeatherAccess', label: 'All-weather road access', type: 'bool',
        hint: 'A single-source dependency behind a bad road becomes unreachable in the rains.' },
      { name: 'lat', label: 'Latitude', type: 'decimal', step: 0.000001 },
      { name: 'lng', label: 'Longitude', type: 'decimal', step: 0.000001 },
      { name: 'isActive', label: 'Active', type: 'bool', default: true },
    ],
  },

  corridors: {
    key: 'corridors',
    title: 'Delivery areas',
    singular: 'Delivery area',
    description:
      'The parts of town you deliver to. Judge each one on its own — an area that is closed cannot take new buyers until it has enough stops to be worth the trip.',
    writeRoles: ['admin', 'ops'],
    hasActive: true,
    searchable: true,
    note:
      'Closing a corridor is a real control: it stops sales signing a buyer into a route that is still too thin to pay for itself.',
    columns: ['code', 'name', 'city', 'targetDrops', 'isOpenForSales', 'buyerCount'],
    derived: [{ name: 'buyerCount', label: 'Buyers' }],
    fields: [
      { name: 'code', label: 'Code', required: true },
      { name: 'name', label: 'Name', required: true },
      { name: 'city', label: 'City' },
      { name: 'depotId', label: 'Served from depot', type: 'ref', ref: { resource: 'depots' } },
      { name: 'targetDrops', label: 'Target drops per van-day', type: 'number',
        hint: 'Below about 12 the last mile does not pay for itself.' },
      { name: 'targetKgPerDay', label: 'Target kg per day', type: 'decimal' },
      { name: 'windowStart', label: 'Delivery window opens', type: 'time' },
      { name: 'windowEnd', label: 'Delivery window closes', type: 'time' },
      { name: 'isOpenForSales', label: 'Open to new buyers', type: 'bool', default: true,
        hint: 'Turn this off until the corridor is dense enough. Sales cannot override it.' },
      { name: 'radiusKm', label: 'Radius', type: 'decimal', step: 0.1, suffix: 'km' },
      { name: 'isActive', label: 'Active', type: 'bool', default: true },
    ],
  },

  // ============================================================ catalogue
  crops: {
    key: 'crops',
    title: 'Crops',
    singular: 'Crop',
    description:
      'What you buy, and how each one behaves: how quickly it dries out, how long it keeps, and what it must not be loaded next to.',
    writeRoles: ['admin', 'ops'],
    hasActive: true,
    searchable: true,
    columns: ['code', 'name', 'category', 'shelfLifeHours', 'targetShrinkPct', 'gradeCount'],
    derived: [{ name: 'gradeCount', label: 'Grades' }],
    filters: [{ name: 'category', label: 'categories', options: CROP_CATEGORY }],
    fields: [
      { name: 'code', label: 'Code', required: true },
      { name: 'name', label: 'Name', required: true },
      { name: 'localName', label: 'Local name', hint: 'What the farmer and the chef actually call it.' },
      { name: 'category', label: 'Category', type: 'select', options: CROP_CATEGORY },
      { name: 'uom', label: 'Unit of measure', hint: 'kg unless there is a reason.' },
      { name: 'shelfLifeHours', label: 'Shelf life', type: 'number', suffix: 'hours',
        hint: 'Sets the expiry clock on every lot of this crop.' },
      { name: 'expectedMoistureLossPct', label: 'Expected moisture loss', type: 'decimal', step: 0.1, suffix: '%',
        hint: 'Leafy crops can lose 5–10% of mass overnight. Price it in rather than arguing about it later.' },
      { name: 'targetShrinkPct', label: 'Target shrink', type: 'decimal', step: 0.1, suffix: '%' },
      { name: 'targetMarginPct', label: 'Target gross margin', type: 'decimal', step: 0.1, suffix: '%' },
      { name: 'volumeToWeightIndex', label: 'Bulk index', type: 'decimal', step: 0.1,
        hint: 'Bulky lines cost more per kilo to move. 1.0 is tomatoes.' },
      { name: 'ethyleneProducer', label: 'Produces ethylene', type: 'bool',
        hint: 'Bananas, avocados and mangoes gas out anything sensitive loaded beside them.' },
      { name: 'ethyleneSensitive', label: 'Sensitive to ethylene', type: 'bool' },
      { name: 'requiresCold', label: 'Requires cold chain', type: 'bool' },
      { name: 'isActive', label: 'Active', type: 'bool', default: true },
    ],
  },

  grades: {
    key: 'grades',
    title: 'Grades',
    singular: 'Grade',
    description:
      'Exactly what counts as Grade A, B and so on, written down. Without this you cannot send somebody else out to buy for you, and you cannot argue when a buyer sends produce back.',
    writeRoles: ['admin', 'ops'],
    searchable: true,
    note:
      '"Good tomatoes" is not a specification. Write the size band, the colour stage and the blemish tolerance, and photograph it.',
    columns: ['cropName', 'code', 'name', 'rank', 'specText', 'isSellable'],
    derived: [{ name: 'cropName', label: 'Crop' }],
    filters: [{ name: 'cropId', label: 'crops', ref: { resource: 'crops' } }],
    fields: [
      { name: 'cropId', label: 'Crop', type: 'ref', ref: { resource: 'crops' }, required: true },
      { name: 'code', label: 'Grade code', required: true, hint: 'A, B, C, REJ' },
      { name: 'name', label: 'Name', required: true },
      { name: 'rank', label: 'Rank', type: 'number', required: true, hint: '1 is best. Drives grade-out accounting.' },
      { name: 'specText', label: 'Specification', type: 'textarea', required: true,
        hint: 'Exactly what a buyer will accept. This is what a rejection is argued against.' },
      { name: 'sizeMinMm', label: 'Minimum size', type: 'decimal', step: 0.1, suffix: 'mm' },
      { name: 'sizeMaxMm', label: 'Maximum size', type: 'decimal', step: 0.1, suffix: 'mm' },
      { name: 'colourStage', label: 'Colour stage', hint: 'breaker, pink, full colour…' },
      { name: 'blemishTolerancePct', label: 'Blemish tolerance', type: 'decimal', step: 0.1, suffix: '%' },
      { name: 'specPhotoUrl', label: 'Specification photo URL' },
      { name: 'isSellable', label: 'Sellable', type: 'bool', default: true },
    ],
  },

  packaging: {
    key: 'packaging',
    title: 'Packaging',
    singular: 'Packaging',
    description:
      'Crates and bags, and what each weighs empty. Always buy by weight, never by the sack — an oversized bag can cost you a third of a load.',
    writeRoles: ['admin', 'ops'],
    hasActive: true,
    searchable: true,
    columns: ['code', 'name', 'tareKg', 'nominalKg', 'isReturnable', 'unitCost'],
    fields: [
      { name: 'code', label: 'Code', required: true },
      { name: 'name', label: 'Name', required: true },
      { name: 'tareKg', label: 'Tare weight', type: 'decimal', step: 0.001, required: true, suffix: 'kg',
        hint: 'Subtracted automatically at every weighing.' },
      { name: 'nominalKg', label: 'Nominal fill', type: 'decimal', step: 0.1, suffix: 'kg' },
      { name: 'isReturnable', label: 'Returnable', type: 'bool', default: true,
        hint: 'Returnable crates need a deposit or they walk — 10–20% a year is normal.' },
      { name: 'unitCost', label: 'Unit cost', type: 'money' },
      { name: 'isActive', label: 'Active', type: 'bool', default: true },
    ],
  },

  scales: {
    key: 'scales',
    title: 'Scales',
    singular: 'Scale',
    description:
      'Your scales, and their certificates. Trading on an uncertified scale is against the law, and it invites arguments from both farmers and buyers.',
    writeRoles: ['admin', 'ops'],
    hasActive: true,
    searchable: true,
    columns: ['code', 'serial', 'model', 'certNumber', 'certExpiry', 'driftFlag', 'weighmentCount'],
    derived: [{ name: 'weighmentCount', label: 'Weighments' }],
    fields: [
      { name: 'code', label: 'Code', required: true },
      { name: 'serial', label: 'Serial number' },
      { name: 'model', label: 'Model' },
      { name: 'locationType', label: 'Location type', type: 'select', options: [
        { value: 'field', label: 'Field' },
        { value: 'depot', label: 'Depot' },
        { value: 'vehicle', label: 'Vehicle' },
      ] },
      { name: 'capacityKg', label: 'Capacity', type: 'decimal', suffix: 'kg' },
      { name: 'resolutionKg', label: 'Resolution', type: 'decimal', step: 0.001, suffix: 'kg' },
      { name: 'certNumber', label: 'Certificate number' },
      { name: 'certExpiry', label: 'Certificate expires', type: 'date' },
      { name: 'lastCalibratedAt', label: 'Last calibrated', type: 'date' },
      { name: 'nextCalibrationDue', label: 'Next calibration due', type: 'date' },
      { name: 'driftFlag', label: 'Flagged for drift', type: 'bool',
        hint: 'Weighments taken while flagged are held for reconciliation and not booked to shrink.' },
      { name: 'isActive', label: 'Active', type: 'bool', default: true },
    ],
  },

  vehicles: {
    key: 'vehicles',
    title: 'Vehicles',
    singular: 'Vehicle',
    description:
      'Your vans and lorries. Hire rather than buy until they have been busy more than 80% of the time for six months straight — an idle vehicle you own still costs you every month.',
    writeRoles: ['admin', 'ops'],
    hasActive: true,
    searchable: true,
    columns: ['registration', 'label', 'bodyType', 'ownership', 'capacityKg', 'dayRate'],
    filters: [{ name: 'ownership', label: 'ownership', options: OWNERSHIP }],
    fields: [
      { name: 'registration', label: 'Registration', required: true },
      { name: 'label', label: 'Label', hint: 'What the team calls it — "Van 1".' },
      { name: 'ownership', label: 'Ownership', type: 'select', options: OWNERSHIP, default: 'contracted' },
      { name: 'bodyType', label: 'Body type', hint: 'pickup, 3t truck, van, reefer' },
      { name: 'capacityKg', label: 'Capacity', type: 'decimal', required: true, suffix: 'kg' },
      { name: 'legalPayloadKg', label: 'Legal payload', type: 'decimal', suffix: 'kg',
        hint: 'Never load above this. Overloading fines corrupt drivers as well as costing money.' },
      { name: 'hasReefer', label: 'Refrigerated', type: 'bool' },
      { name: 'gpsDeviceId', label: 'GPS device ID' },
      { name: 'contractorName', label: 'Contractor' },
      { name: 'dayRate', label: 'Day rate', type: 'money' },
      { name: 'perKmRate', label: 'Per-km rate', type: 'money' },
      { name: 'isActive', label: 'Active', type: 'bool', default: true },
    ],
  },

  'cess-rates': {
    key: 'cess-rates',
    title: 'County charges',
    singular: 'County charge',
    description:
      'What each county charges to move produce across its boundary. Put it in here and it gets costed into the route instead of surprising a driver on the road.',
    writeRoles: ['admin', 'ops'],
    searchable: false,
    columns: ['countyName', 'cropName', 'basis', 'rate', 'effectiveFrom', 'effectiveTo'],
    derived: [{ name: 'countyName', label: 'County' }, { name: 'cropName', label: 'Crop' }],
    filters: [{ name: 'cropId', label: 'crops', ref: { resource: 'crops' } }],
    fields: [
      { name: 'countyCode', label: 'County', type: 'ref', required: true,
        ref: { resource: 'reference/counties', labelField: 'name' } },
      { name: 'cropId', label: 'Crop', type: 'ref', ref: { resource: 'crops' },
        hint: 'Leave empty to apply to every crop.' },
      { name: 'basis', label: 'Charged', type: 'select', options: CESS_BASIS, required: true },
      { name: 'rate', label: 'Rate', type: 'money', required: true },
      { name: 'effectiveFrom', label: 'Effective from', type: 'date' },
      { name: 'effectiveTo', label: 'Effective to', type: 'date' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },

  // ============================================================ supply
  farms: {
    key: 'farms',
    title: 'Farms',
    singular: 'Farm',
    description:
      'The plots each farmer works. Write down who actually has the right to sell the produce — it saves an argument over payment later, especially with fruit trees.',
    writeRoles: ['admin', 'ops', 'field_agent'],
    searchable: false,
    columns: ['farmerName', 'name', 'acreage', 'tenure', 'hasRightToSell', 'allWeatherAccess'],
    derived: [{ name: 'farmerName', label: 'Farmer' }],
    fields: [
      { name: 'farmerId', label: 'Farmer', type: 'ref', required: true,
        ref: { resource: 'farmers', labelField: 'fullName' } },
      { name: 'name', label: 'Farm name' },
      { name: 'acreage', label: 'Acreage', type: 'decimal', step: 0.01 },
      { name: 'tenure', label: 'Tenure', type: 'select', options: TENURE },
      { name: 'hasRightToSell', label: 'Has the right to sell', type: 'bool', default: true,
        hint: 'A tenant or family member may not. Verify before pre-financing anything.' },
      { name: 'allWeatherAccess', label: 'All-weather access', type: 'bool' },
      { name: 'lat', label: 'Latitude', type: 'decimal', step: 0.000001 },
      { name: 'lng', label: 'Longitude', type: 'decimal', step: 0.000001 },
    ],
  },

  'payout-accounts': {
    key: 'payout-accounts',
    title: 'Farmer M-Pesa numbers',
    singular: 'M-Pesa number',
    description:
      'The M-Pesa number each farmer is paid on. The name on the account is checked against the farmer\'s name, and if it does not match nothing goes out.',
    writeRoles: ['admin', 'ops'],
    hasActive: true,
    searchable: false,
    note:
      'The name-match score is computed by the server and cannot be set from here. Sending money to an account in someone else’s name is the dispute you cannot win.',
    columns: ['farmerName', 'channel', 'accountRef', 'accountName', 'nameMatchState', 'isPrimary'],
    derived: [{ name: 'farmerName', label: 'Farmer' }],
    filters: [{ name: 'farmerId', label: 'farmers', ref: { resource: 'farmers', labelField: 'fullName' } }],
    fields: [
      { name: 'farmerId', label: 'Farmer', type: 'ref', required: true,
        ref: { resource: 'farmers', labelField: 'fullName' } },
      { name: 'channel', label: 'Channel', type: 'select', options: PAYOUT_CHANNEL, default: 'mpesa' },
      { name: 'accountRef', label: 'Account / phone number', required: true },
      { name: 'accountName', label: 'Name on the account', required: true,
        hint: 'Exactly as the provider holds it, not as you would write it.' },
      { name: 'bankCode', label: 'Bank code' },
      { name: 'isPrimary', label: 'Primary account', type: 'bool' },
      { name: 'isActive', label: 'Active', type: 'bool', default: true },
    ],
  },

  // ============================================================ demand
  'buyer-contacts': {
    key: 'buyer-contacts',
    title: 'Buyer contacts',
    singular: 'Contact',
    description:
      'Who to speak to at each buyer, and what they do there. Whether you are talking to the chef or the owner changes the whole conversation.',
    writeRoles: ['admin', 'ops', 'sales'],
    searchable: true,
    columns: ['buyerName', 'fullName', 'role', 'msisdn', 'receivesInvoice', 'receivesDelivery'],
    derived: [{ name: 'buyerName', label: 'Buyer' }],
    filters: [{ name: 'buyerId', label: 'buyers', ref: { resource: 'buyers' } }],
    fields: [
      { name: 'buyerId', label: 'Buyer', type: 'ref', ref: { resource: 'buyers' }, required: true },
      { name: 'fullName', label: 'Name', required: true },
      { name: 'role', label: 'Role', hint: 'chef, storekeeper, owner, finance' },
      { name: 'msisdn', label: 'Phone' },
      { name: 'email', label: 'Email' },
      { name: 'isPrimary', label: 'Primary contact', type: 'bool' },
      { name: 'receivesInvoice', label: 'Receives invoices', type: 'bool' },
      { name: 'receivesDelivery', label: 'Receives deliveries', type: 'bool' },
    ],
  },

  'buyer-contracts': {
    key: 'buyer-contracts',
    title: 'Buyer contracts',
    singular: 'Contract',
    description:
      'How a buyer\'s price moves with the market, and how much they have to order to get it.',
    writeRoles: ['admin', 'ops', 'sales'],
    searchable: true,
    note:
      'A fuel clause and a minimum order value are boring, standard, and routinely omitted until diesel moves 25% or a buyer starts cherry-picking.',
    columns: ['code', 'buyerName', 'validFrom', 'validTo', 'pricingMode', 'minOrderValue', 'status'],
    derived: [{ name: 'buyerName', label: 'Buyer' }, { name: 'priceCount', label: 'Prices' }],
    filters: [
      { name: 'buyerId', label: 'buyers', ref: { resource: 'buyers' } },
      { name: 'status', label: 'statuses', options: CONTRACT_STATUS },
    ],
    fields: [
      { name: 'buyerId', label: 'Buyer', type: 'ref', ref: { resource: 'buyers' }, required: true },
      { name: 'code', label: 'Code', hint: 'Left blank, one is generated.' },
      { name: 'validFrom', label: 'Valid from', type: 'date', required: true },
      { name: 'validTo', label: 'Valid to', type: 'date' },
      { name: 'pricingMode', label: 'Pricing mode', type: 'select', options: PRICING_MODE },
      { name: 'minOrderValue', label: 'Minimum order value', type: 'money',
        hint: 'Stops a buyer ordering only the lines where you beat the market.' },
      { name: 'indexSource', label: 'Index source', hint: 'e.g. Marikiti daily average' },
      { name: 'collarLowPct', label: 'Collar low', type: 'decimal', step: 0.1, suffix: '%' },
      { name: 'collarHighPct', label: 'Collar high', type: 'decimal', step: 0.1, suffix: '%' },
      { name: 'fuelClauseEnabled', label: 'Fuel adjustment clause', type: 'bool' },
      { name: 'fuelBasePrice', label: 'Fuel base price', type: 'money' },
      { name: 'fuelTriggerPct', label: 'Fuel trigger', type: 'decimal', step: 0.1, suffix: '%' },
      { name: 'status', label: 'Status', type: 'select', options: CONTRACT_STATUS, default: 'draft' },
      { name: 'signedAt', label: 'Signed', type: 'date' },
    ],
  },

  'buyer-prices': {
    key: 'buyer-prices',
    title: 'Contract prices',
    singular: 'Contract price',
    description: 'Prices agreed with one buyer in writing. These beat your normal selling price for that buyer.',
    writeRoles: ['admin', 'ops', 'sales'],
    searchable: false,
    columns: ['cropName', 'gradeCode', 'pricePerKg', 'effectiveFrom', 'effectiveTo'],
    derived: [{ name: 'cropName', label: 'Crop' }, { name: 'gradeCode', label: 'Grade' }],
    filters: [{ name: 'contractId', label: 'contracts', ref: { resource: 'buyer-contracts', labelField: 'code' } }],
    fields: [
      { name: 'contractId', label: 'Contract', type: 'ref', required: true,
        ref: { resource: 'buyer-contracts', labelField: 'code' } },
      { name: 'cropId', label: 'Crop', type: 'ref', ref: { resource: 'crops' }, required: true },
      { name: 'gradeId', label: 'Grade', type: 'ref', ref: { resource: 'grades', labelField: 'code' }, required: true },
      { name: 'pricePerKg', label: 'Price per kg', type: 'money', required: true },
      { name: 'effectiveFrom', label: 'Effective from', type: 'date' },
      { name: 'effectiveTo', label: 'Effective to', type: 'date' },
    ],
  },

  // ============================================================ finance
  expenses: {
    key: 'expenses',
    title: 'Expenses',
    singular: 'Expense',
    description:
      'Fuel, rent, wages, packaging — everything you spend. Leave these out and the profit per kilo on the money pages is made up.',
    writeRoles: ['admin', 'finance'],
    searchable: true,
    note:
      'Unprocessed produce is VAT-exempt, so input VAT on fuel, rent and vehicles is a cost and not a receivable. Record it under "Irrecoverable input VAT" and it lands in the waterfall.',
    columns: ['expenseDate', 'category', 'amount', 'supplierName', 'reference', 'countyCode'],
    filters: [{ name: 'category', label: 'categories', options: EXPENSE_CATEGORY }],
    fields: [
      { name: 'expenseDate', label: 'Date', type: 'date', required: true },
      { name: 'category', label: 'Category', type: 'select', options: EXPENSE_CATEGORY, required: true },
      { name: 'amount', label: 'Amount', type: 'money', required: true },
      { name: 'inputVat', label: 'Input VAT included', type: 'money',
        hint: 'On an exempt supply this is not reclaimable — it is part of your cost base.' },
      { name: 'isVatRecoverable', label: 'VAT is recoverable', type: 'bool' },
      { name: 'supplierName', label: 'Supplier' },
      { name: 'reference', label: 'Reference' },
      { name: 'countyCode', label: 'County', type: 'ref', ref: { resource: 'reference/counties', labelField: 'name' } },
      { name: 'vehicleId', label: 'Vehicle', type: 'ref', ref: { resource: 'vehicles', labelField: 'registration' } },
      { name: 'depotId', label: 'Depot', type: 'ref', ref: { resource: 'depots' } },
      { name: 'receiptUrl', label: 'Receipt URL' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },

  // ============================================================ admin
  'variance-reasons': {
    key: 'variance-reasons',
    title: 'Reasons weight goes missing',
    singular: 'Reason',
    description:
      'The reasons weight goes missing. Keeping them separate means theft can never be quietly written off as spoilage.',
    writeRoles: ['admin'],
    searchable: true,
    note:
      'Only the unexplained bucket should carry the fraud signal — and it must never read zero, which would mean reasons are being assigned to make the number go away.',
    columns: ['code', 'label', 'bucket', 'isFraudSignal', 'requiresOwner', 'sortOrder'],
    fields: [
      { name: 'code', label: 'Code', required: true, readOnly: false },
      { name: 'label', label: 'Label', required: true },
      { name: 'bucket', label: 'Bucket', type: 'select', options: SHRINK_BUCKET, required: true },
      { name: 'isFraudSignal', label: 'Counts as a fraud signal', type: 'bool' },
      { name: 'requiresOwner', label: 'Requires a responsible person', type: 'bool' },
      { name: 'sortOrder', label: 'Sort order', type: 'number' },
    ],
  },
}

/** Grouped for the navigation rail. */
export const RESOURCE_GROUPS: Array<{ group: string; keys: string[] }> = [
  { group: 'Catalogue', keys: ['crops', 'grades', 'packaging'] },
  { group: 'Network', keys: ['zones', 'collection-centres', 'depots', 'corridors'] },
  { group: 'Assets', keys: ['scales', 'vehicles'] },
  { group: 'Supply', keys: ['farms', 'payout-accounts'] },
  { group: 'Demand', keys: ['buyer-contacts', 'buyer-contracts', 'buyer-prices'] },
  { group: 'Money', keys: ['expenses', 'cess-rates'] },
  { group: 'Rules', keys: ['variance-reasons'] },
]
